import * as React from "react";
import { useCallback, useMemo } from "react";
import { Form, Formik } from "formik";
import { Input, Modal } from "components/bootstrap";
import { FormikInput, ModalSubmit, Select } from "components/common";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useUpdateWorkflowConfig from "./hooks/useUpdateWorkflowConfig";
const inputType = (type) => {
  switch (type) {
    case "INT":
      return "number";
    default:
      return "text";
  }
};
export const initialValuesFor = (params) => params.reduce(
  (values, param) => ({
    ...values,
    [param.id]: param.value
  }),
  {}
);
export const normalizeValues = (values, params) => params.reduce((normalizedValues, param) => {
  const value = values[param.id];
  if (value === "" || value === null) {
    return { ...normalizedValues, [param.id]: value };
  }
  if (param.type === "INT") {
    return { ...normalizedValues, [param.id]: Number(value) };
  }
  return { ...normalizedValues, [param.id]: value };
}, {});
const ConfigField = ({ param }) => /* @__PURE__ */ React.createElement(
  FormikInput,
  {
    id: param.id,
    name: param.id,
    label: param.title,
    help: param.description,
    type: param.type === "BOOLEAN" ? "checkbox" : inputType(param.type)
  }
);
const SelectConfigField = ({
  param,
  value,
  onChange
}) => {
  var _a;
  return /* @__PURE__ */ React.createElement(Input, { id: param.id, label: param.title, help: param.description }, /* @__PURE__ */ React.createElement(
    Select,
    {
      inputId: param.id,
      value,
      options: (_a = param.options) != null ? _a : [],
      displayKey: "title",
      valueKey: "value",
      onChange
    }
  ));
};
export const WorkflowConfigurationFormFields = ({
  params,
  values,
  setFieldValue
}) => /* @__PURE__ */ React.createElement(React.Fragment, null, params.map(
  (param) => {
    var _a;
    return ((_a = param.options) == null ? void 0 : _a.length) ? /* @__PURE__ */ React.createElement(
      SelectConfigField,
      {
        key: param.id,
        param,
        value: values[param.id],
        onChange: (value) => setFieldValue(param.id, value)
      }
    ) : /* @__PURE__ */ React.createElement(ConfigField, { key: param.id, param });
  }
));
const EditWorkflowConfigurationModal = ({ workflow, onClose }) => {
  const sendTelemetry = useSendTelemetry();
  const initialValues = useMemo(() => initialValuesFor(workflow.config), [workflow.config]);
  const { mutateAsync: updateWorkflowConfig } = useUpdateWorkflowConfig();
  const onSubmit = useCallback(
    (values) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.AI_AGENT_WORKFLOW.WORKFLOW_CONFIGURATION_SAVED, {
        app_section: "aiagent-workflows",
        event_details: { workflow_type: workflow.id }
      });
      return updateWorkflowConfig({ workflow, config: normalizeValues(values, workflow.config) }).then(() => {
        onClose();
      });
    },
    [onClose, sendTelemetry, updateWorkflowConfig, workflow]
  );
  return /* @__PURE__ */ React.createElement(Modal, { onHide: onClose, show: true }, /* @__PURE__ */ React.createElement(Formik, { initialValues, onSubmit }, ({ isSubmitting, setFieldValue, values }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, "Edit ", workflow.title)), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(WorkflowConfigurationFormFields, { params: workflow.config, values, setFieldValue })), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      submitButtonText: "Update workflow",
      submitLoadingText: "Updating workflow...",
      onCancel: onClose,
      isAsyncSubmit: true,
      isSubmitting
    }
  )))));
};
export default EditWorkflowConfigurationModal;
